/* global trapFocus removeTrapFocus */

/**
 * @description <header-search-opener>
 * 搜索按钮组件，用于打开搜索弹窗
 */
class HeaderSearchOpener extends HTMLElement {
  constructor() {
    super()
    this.addEventListener('click', this.openSearch.bind(this))
  }

  connectedCallback() {
    this.setAttribute('aria-expanded', 'false')
    this.setAttribute('aria-haspopup', 'dialog')
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Search')
    }
    if (!this.hasAttribute('aria-controls')) {
      this.setAttribute('aria-controls', 'header-search-modal')
    }
  }

  openSearch() {
    const searchModalId = this.getAttribute('aria-controls') || 'header-search-modal'
    const searchModal = document.getElementById(searchModalId)
    if (!searchModal) return

    if (searchModal.tagName.toLowerCase() === 'header-search') {
      searchModal.open(this)
    } else {
      // 兼容旧版本
      searchModal.classList.remove('closing')
      searchModal.setAttribute('open', '')
      this.setAttribute('aria-expanded', 'true')
      document.body.style.setProperty('overflow-y', 'hidden')

      const searchInput = searchModal.querySelector('input[type="search"]')
      if (searchInput) {
        setTimeout(() => searchInput.focus(), 100)
      }
    }
  }
}

if (!customElements.get('header-search-opener')) {
  customElements.define('header-search-opener', HeaderSearchOpener)
}

/**
 * @description <header-search>
 * 搜索弹窗组件，从顶部滑到底部
 */
class HeaderSearch extends HTMLElement {
  constructor() {
    super()
    this.openedBy = null
    this.closeTimer = null
    this.searchInput = null
    this.setupEventListeners()
  }

  connectedCallback() {
    // 确保组件在 body 中
    if (this.moved) return
    this.moved = true
    if (this.parentElement !== document.body) {
      document.body.appendChild(this)
    }

    // 设置初始属性
    this.setAttribute('role', 'dialog')
    this.setAttribute('aria-modal', 'true')
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Search')
    }
    if (!this.id) {
      this.id = 'header-search-modal'
    }
    this.searchInput = this.querySelector('input[type="search"]')
  }

  setupEventListeners() {
    // 关闭按钮
    const closeButtons = this.querySelectorAll('[id^="SearchClose-"]')
    if (closeButtons) {
      closeButtons.forEach(button => {
        button.addEventListener('click', this.close.bind(this))
      })
    }

    // 点击背景关闭
    this.addEventListener('click', event => {
      if (event.target === this) {
        this.close()
      }
    })

    // ESC 键关闭
    this.addEventListener('keyup', event => {
      if (event.code === 'Escape') {
        this.close()
      }
    })
  }

  open(opener) {
    if (opener) {
      this.openedBy = opener
    }
    clearTimeout(this.closeTimer)
    this.classList.remove('closing')
    this.setAttribute('open', '')

    if (this.openedBy) {
      this.openedBy.setAttribute('aria-expanded', 'true')
    }

    document.body.classList.add('overflow-hidden')

    // 自动聚焦搜索输入框
    if (!this.searchInput) return

    const handleTransitionEnd = event => {
      // 确保是目标元素的 transition 结束事件
      if (event.target !== this) return
      trapFocus(this, this.searchInput)
    }

    this.addEventListener('transitionend', handleTransitionEnd, { once: true })
  }

  close() {
    if (this.classList.contains('closing')) return

    this.classList.add('closing')
    this.removeAttribute('open')

    if (this.openedBy) {
      this.openedBy.setAttribute('aria-expanded', 'false')
    }

    clearTimeout(this.closeTimer)
    this.closeTimer = setTimeout(() => {
      this.classList.remove('closing')
    }, 300)

    document.body.classList.remove('overflow-hidden')

    // 焦点回到打开按钮
    if (this.openedBy) this.openedBy.focus()
    if (this.searchInput) this.searchInput.blur()
    removeTrapFocus(this)
  }
}

if (!customElements.get('header-search')) {
  customElements.define('header-search', HeaderSearch)
}
